import React, { useRef } from 'react';
import { Box, Divider, Typography, FormControl, RadioGroup, FormControlLabel, Radio, Paper, alpha, useTheme } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  FactsDescriptionField,
  PieceToRebutField,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

// Opções para cumprimento de sentença
const PECAS_BASE = [
  'Sentença',
  'Acórdão',
  'Decisão Homologatória',
  'Título Executivo Judicial',
  'Outro'
];

// Opções de cálculo
const OPCOES_CALCULO = [
  { value: 'anexado', label: 'Sim, anexei acima' },
  { value: 'fazer', label: 'Não, quero fazer o cálculo' },
  { value: 'sem_calculo', label: 'Essa execução não possui cálculo' },
];

const CumprimentoSentencaForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Cumprimento de Sentença"
        customDescription="Execução de título executivo judicial"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
        />

        {/* 2. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Cálculos, Processo na íntegra e Sentença."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* 3. Pergunta sobre cálculo de liquidação */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Você já possui e enviou o cálculo de liquidação?
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={values.status_calculo || ''}
              onChange={(e) => handleFieldChange('status_calculo', e.target.value)}
            >
              {OPCOES_CALCULO.map((opcao) => (
                <Paper
                  key={opcao.value}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    p: 1.5,
                    cursor: 'pointer',
                    border: values.status_calculo === opcao.value 
                      ? `2px solid ${theme.palette.primary.main}` 
                      : '1px solid',
                    borderColor: values.status_calculo === opcao.value 
                      ? 'primary.main' 
                      : 'grey.300',
                    bgcolor: values.status_calculo === opcao.value 
                      ? alpha(theme.palette.primary.main, 0.04) 
                      : 'transparent',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }}
                  onClick={() => handleFieldChange('status_calculo', opcao.value)}
                >
                  <FormControlLabel
                    value={opcao.value}
                    control={<Radio size="small" />}
                    label={opcao.label}
                    sx={{ m: 0, width: '100%' }}
                  />
                </Paper>
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {/* 4. Descrição dos Fatos (Opcional) */}
        <FactsDescriptionField
          value={values.fatos_caso || ''}
          onChange={(value) => handleFieldChange('fatos_caso', value)}
          placeholder="Descreva os valores devidos, período de cálculo, índices de correção aplicáveis, parcelas em atraso..."
        />

        {/* 5. Peça base */}
        <PieceToRebutField
          value={values.peca_rebater || 'Sentença'}
          onChange={(value) => handleFieldChange('peca_rebater', value)}
          otherValue={values.peca_rebater_outro || ''}
          onOtherChange={(value) => handleFieldChange('peca_rebater_outro', value)}
          options={PECAS_BASE}
          label="Indique a peça do processo que deseja rebater ou responder:"
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os valores e parcelas a serem executados que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Anexe a sentença ou acórdão exequendo e, se possível, a memória de cálculo. Quanto mais completa a documentação, mais precisa será a peça de cumprimento de sentença gerada." />
    </Box>
  );
};

export default CumprimentoSentencaForm;

